import { gql } from '@apollo/client'

export const GET_USER_PROFILE = gql`
  query getUserProfile {
    getUserProfile {
      userId
      lastName
      firstName
      homeAddress
      countryCode
      onboardingType
      phone
      phoneCountryCode
      theme
      title
      workStatus
      onboardedAt
      inviteCompletedAt
      accessExpiresAt
      filloutUser
      yearsOfExperience
      occupation {
        slug
        baseTitlePlusSpecialty
        professionId
        specialtyId
        profession {
          name
          id
          slug
        }
        specialty {
          id
          baseSpecialtyId
          name
          slug
        }
      }
    }
  }
`

export const UPSERT_USER_PROFILE = gql`
  mutation upsertUserProfile($input: UserProfileInput!) {
    upsertUserProfile(input: $input) {
      firstName
      lastName
      onboardingType
      phone
      phoneCountryCode
      theme
      title
      countryCode
      homeAddress
    }
  }
`

/** only on internal client */
export const GET_USER_BY_EMAIL = gql`
  query getUserByEmail($email: String!) {
    getUserByEmail(email: $email) {
      id
    }
  }
`

/**
 * NOTE: if you add or remove from this, ensure you
 * also add to the {@link GET_SURVEY_REVIEW} and/or {@link SEARCH_SALARIES}
 * and/or {@link SEARCH_SALARIES_FOR_HOME} query
 */
export const GET_USER_SALARIES = gql`
  query getSalaries {
    getSalaries {
      academicAffiliation
      anyBonuses
      anyOtherIncome
      avgHoursWorked
      baseSalary
      basePayPerHour
      benefitsWhenJoined
      bonus
      callPaid
      callPaidRate
      callSchedule
      callScheduleOther
      callScheduleDetails
      callType
      clinicalPct
      cmeAllowance
      cmeTimeWeeks
      collectionsPct
      compLoanAssistance
      compModel
      compOtherDetails
      compSatisfaction
      compTargetWrvu
      postedAt
      dayOffPostCall
      retirementPlans
      employerType
      employerTypeDetails
      estimatedFte
      jobType
      otherBonus
      otherIncome
      otherNotableBenefits
      otherNoteworthyComp
      overtimeIncome
      overtimeRatePerHour
      productivityBonus
      qualityBonus
      relocationBonus
      responsibilities
      salaryId
      scheduledHours
      signingBonus
      taxStatus
      teachingPct
      wrvuRate
      yearsOfExperience
      displayLocation
      occupation {
        id
        slug
        fullTitle
        baseTitlePlusSpecialty
        professionId
        specialtyId
        profession {
          id
          name
          slug
          alias
        }
        specialty {
          id
          baseSpecialtyId
          name
          fullName
        }
      }
    }
  }
`

export const CREATE_SURVEY_SUBMISSION = gql`
  mutation createSurveySubmission($surveyName: String!) {
    createSurveySubmission(surveyName: $surveyName) {
      id
    }
  }
`

export const DELETE_SALARY = gql`
  mutation deleteSalary($salaryId: UUID!) {
    deleteSalary(salaryId: $salaryId)
  }
`

export const GET_SALARY_SUBMISSIONS = gql`
  query getSalarySubmissions {
    getSalarySubmissions {
      id
      createdAt
      updatedAt
      surveyId
      userId
      pageId
      status
      responses
    }
  }
`

export const DELETE_SALARY_SUBMISSION = gql`
  mutation deleteSalarySubmission($salaryId: UUID!) {
    deleteSalarySubmission(salaryId: $salaryId)
  }
`

export const DELETE_SURVEY_SUBMISSION = gql`
  mutation deleteSurveySubmission($surveySubmissionId: UUID!) {
    deleteSurveySubmission(surveySubmissionId: $surveySubmissionId)
  }
`

export const EDIT_SALARY = gql`
  mutation editSalary($salaryId: UUID!) {
    editSalary(salaryId: $salaryId) {
      submissionId
      # this is needed as an identifier for Apollo
      salaryId
    }
  }
`

export const GET_SALARY = gql`
  query getSalary($salaryId: UUID!) {
    getSalary(salaryId: $salaryId) {
      academicAffiliation
      ancillaryIncomeSource
      annualEstAncillaryIncome
      annualEstRankAdjustment
      annualEstPartnerIncome
      annualLeadershipStipend
      annualNonClinicalIncome
      anyBonuses
      anyOtherIncome
      approvalStatus
      avgHoursWorked
      baseSalary
      basePayPerHour
      benefitsWhenJoined
      bonus
      callPaid
      callPaidRate
      callSchedule
      callScheduleOther
      callScheduleDetails
      callType
      clinicalPct
      cmeAllowance
      cmeTimeWeeks
      collectionsPct
      compLoanAssistance
      compModel
      compOtherDetails
      compSatisfaction
      compTargetWrvu
      compTotal {
        base
        bonus
        other
        total
        annualBonus
        annualOther
      }
      dayOffPostCall
      employer401aMatch
      employer401kMatch
      employer403bMatch
      employer457bMatch
      employerCoversMalpractice
      employerType
      employerTypeDetails
      estimatedFte
      hourlyRateDifferential
      insuranceHealthWellnessBenefits
      jobType
      leadershipPct
      locums {
        isCallStipend
        agencyBenefits
        agencyFeedback
        agencyName
        agencyRating
        callRatePerHour
        callStipendWeekendRate
        callStipendWeeklyRate
        contractActiveThruDate
        contractingEntity
        employerName
        guaranteedHrsPerDay
      }
      loanAssistance
      newJob
      nightlyHourlyRate
      occupationId
      otherBonus
      otherIncome
      otherNotableBenefits
      otherNoteworthyComp
      overtimeIncome
      overtimeRatePerHour
      paidTimeOffWeeks
      parentalLeaveWeeks
      pensionContribution
      postedAt
      practiceSetting
      practiceSettingOther
      productivityBonus
      qualityBonus
      relocationBonus
      retirementPlans
      researchPct
      residencyStipend
      responsibilities
      salaryId
      scheduledDays
      scheduledHours
      shiftTiming
      signingBonus
      submittedByCurrentUser
      tailCoverage
      taxStatus
      teachingPct
      userLikes
      visibility
      visibilityReason
      weekendHourlyRate
      wrvuRate
      yearsOfExperience
      locationDisplayLevel
      displayLocation
      occupation {
        id
        slug
        fullTitle
        baseTitlePlusSpecialty
        professionId
        specialtyId
        profession {
          id
          name
          slug
          alias
        }
        specialty {
          id
          baseSpecialtyId
          name
          fullName
        }
      }
    }
  }
`
